import _ from 'underscore';
import Promise from 'bluebird';

import { convertAspectRatioToDecimal } from '@bingads-webui-campaign/media-common-utils';
import {
  ImageExperienceImage, constants as C
} from '@bingads-webui-campaign-react/image-experience-model';

import {
  getAutoCroppedImages,
  getAutoCroppedImagesForRatios,
  processCroppedImages
} from './get-cropped-image';
import { validateSmartCropSettings } from './validate-crop-settings';
/**
 * Return the image objects
 *
 * @param {Object} mediaRepository - Media repository to get smart crop settings
 * @param {Object} originalImageToCrop - ImageExperienceImage Object
 * @param {string} originalImageToCrop.Src - source of the image
 * @param {Number} originalImageToCrop.width - width of the image
 * @param {Number} originalImageToCrop.height - height of the image
 * @param {Object} smartCropActivity - instrumentation for smart crop
 * @param {Object} isMultimediaAd - true if current is mma
 * @param {Object} isMultimediaAdinMSANEnabled - true if current is msan mma
 * @returns {Array} - Array of image objects
 */
export const getSmartCroppedImagesAsync = ({
  mediaRepository,
  originalImageToCrop,
  smartCropActivity,
  isMultimediaAd,
  isMultimediaAdinMSANEnabled,
  imageOption = C.defaultImageOption,
  aspectRatioOption = C.defaultAspectRatioOption,
  isImageAdExtensionMode,
  isCombinedMsanAndMmaAspectRatios,
  isMMAV2Enabled,
}) => {
  /* turn off smart crop for msan temporarily
   * When on, it will bring rapidly increased associations to MT. Need more discussion.
   *
   * There are some coupled logics. For simplicity, turn off here instead of consuming codes
   */
  if (isMultimediaAdinMSANEnabled || isImageAdExtensionMode) {
    return Promise.resolve(getAutoCroppedImages({
      originalImageToCrop,
      isMultimediaAd,
      isMultimediaAdinMSANEnabled,
      imageOption,
      aspectRatioOption,
      isCombinedMsanAndMmaAspectRatios,
      isMMAV2Enabled,
    }));
  }

  const aspectRatios = C.getAspectRatios({
    isMultimediaAd,
    isMultimediaAdinMSANEnabled,
    imageOption,
  });
  const defaultRatio = imageOption === C.imageOptionKeys.Logo && aspectRatioOption ?
    _.result(aspectRatios, aspectRatioOption) :
    C.getDefaultRatio(originalImageToCrop.Width, originalImageToCrop.Height, aspectRatios);

  const placementSettings = _.map(aspectRatios, cropInfo => ({
    aspectRatio: cropInfo.aspectRatio,
    type: cropInfo.type,
    associationType: cropInfo.associationType,
    stringKey: cropInfo.stringKey,
    previewTypeStringKey: cropInfo.previewTypeStringKey,
    imagePreviewHeight: cropInfo.imagePreviewHeight,
    minSize: cropInfo.minSize,
    previewSettings: cropInfo.previewSettings,
  }));
  let imagesToReturn = [];
  const aspectRatiosWithSmartCrop = {};

  return mediaRepository.generateSmartCrop({
    imageUrl: originalImageToCrop.Src,
    placementSettings,
  }).then((assets) => {
    imagesToReturn = _.compact(_.map(assets, (asset) => {
      const croppedImageData = {
        id: originalImageToCrop.Id,
        idString: originalImageToCrop.IdString,
        src: originalImageToCrop.Src,
        width: originalImageToCrop.width,
        height: originalImageToCrop.height,
        source: originalImageToCrop.source,
      };
      const cropSettings = {
        SourceWidth: asset.Width,
        SourceHeight: asset.Height,
        SourceX: asset.X,
        SourceY: asset.Y,
      };
      croppedImageData.cropSettings = cropSettings;
      const ratioDecimalStr = convertAspectRatioToDecimal(asset.AspectRatio);
      const placementSetting = _.find(aspectRatios, ratio =>
        ratio.aspectRatio.toString() === ratioDecimalStr);
      const minSize = _.propertyOf(placementSetting)('minSize') || [];

      croppedImageData.type = _.propertyOf(placementSetting)('type');
      croppedImageData.associationType = _.propertyOf(placementSetting)('associationType');
      croppedImageData.isLogo = _.propertyOf(placementSetting)('isLogo');
      croppedImageData.isIncluded = defaultRatio &&
        ratioDecimalStr === _.result(defaultRatio, 'aspectRatio').toString();

      if (!validateSmartCropSettings({
        cropSettings,
        originalImage: originalImageToCrop,
        aspectRatio: _.propertyOf(placementSetting)('aspectRatio'),
        minWidth: minSize.length === 2 && minSize[0],
        minHeight: minSize.length === 2 && minSize[1],
        smartCropActivity,
      })) {
        return null;
      }

      aspectRatiosWithSmartCrop[croppedImageData.type] = true;

      return new ImageExperienceImage(croppedImageData);
    }));
  }).catch(() => {
    // swallow the error
  }).then(() => {
    if (imagesToReturn.length < Object.keys(aspectRatios).length) {
      const missingAspectRatios =
        _.pick(aspectRatios, ({ type }) => !aspectRatiosWithSmartCrop[type]);
      const autoCropSettings =
        getAutoCroppedImagesForRatios({
          originalImageToCrop,
          aspectRatios: missingAspectRatios,
          defaultRatioToInclude: defaultRatio,
        });

      imagesToReturn = imagesToReturn.concat(autoCropSettings);
    }

    processCroppedImages({
      croppedImages: imagesToReturn,
      isMultimediaAdinMSANEnabled,
      aspectRatios,
    });

    return imagesToReturn;
  });
};
